/**
 * @package   ScandiPWA\GtmGraphQl
 * @author    Abdelrahman Hady <abdelrahman.hady@scandiweb.com>
 * @copyright Copyright (c) 2023 Scandiweb, Inc (https://scandiweb.com)
 * @license   http://opensource.org/licenses/OSL-3.0 The Open Software License 3.0 (OSL-3.0)
 */

import Event, { EVENT_GTM_SELECT_PROMOTION_GA4, EVENT_GTM_VIEW_PROMOTION_GA4 } from '../../util/Event';

export const TRIGGER_PROMOTION_EVENT_DELAY = 1500;
export const CONTENTS_CUSTOM_CSS_CLASS = '.promo-content-custom-class';

export const getPromoData = (content, elemId) => {
    const position = content.parentElement.dataset.componentPosition
        || content.parentElement.parentElement.dataset.componentPosition;
    const arr = elemId.split('_');
    const contentId = arr.pop();
    const contentName = content.dataset.contentTitle;
    const contentType = arr.join(' ');

    return {
        contentId, contentName, contentType, position, elemId
    };
};

export const handlePromotionEventsTriggerOnClick = (event) => {
    const targetEl = event.target;
    const content = targetEl.closest(CONTENTS_CUSTOM_CSS_CLASS);
    const elemId = content.id || content.dataset.id;
    const arr = elemId.split('_');
    arr.pop();
    const linkSelector = `a.${arr.join('-')}__link`;

    if (targetEl.src || targetEl.href || targetEl.tagName === 'BUTTON' || targetEl.closest(linkSelector)) {
        Event.dispatch(EVENT_GTM_SELECT_PROMOTION_GA4, getPromoData(content, elemId));
    }
};

export const handlePromotionEventsTrigger = (triggeredEvents, contents) => {
    contents.forEach((content) => {
        // eslint-disable-next-line max-len
        const pageHeightToMiddleOfElement = window.scrollY + content.getBoundingClientRect().top + content.clientHeight / 2;
        const elemId = content.id || content.dataset.id;

        if (
            window.scrollY + window.innerHeight >= pageHeightToMiddleOfElement
            && triggeredEvents.indexOf(elemId) === -1
        ) {
            Event.dispatch(EVENT_GTM_VIEW_PROMOTION_GA4, getPromoData(content, elemId));

            triggeredEvents.push(elemId);
        }
    });
};

export const addClickEventListenerToContents = (contentIds, contents) => {
    contents.forEach((content) => {
        const contentId = content.id || content.dataset.id;

        if (!contentIds.find((id) => id === contentId)) {
            contentIds.push(contentId);

            content.addEventListener('click', handlePromotionEventsTriggerOnClick);
        }
    });
};

export const handlePromotionEventsTriggerOnScroll = () => {
    const contents = document.querySelectorAll(CONTENTS_CUSTOM_CSS_CLASS);

    handlePromotionEventsTrigger(window.triggeredEvents, contents);
    addClickEventListenerToContents(window.contentIds, contents);
};

export const componentDidMount = (args, callback, instance) => {
    instance.setState({
        triggeredEvents: [],
        contentIds: [],
        handledPromotionEvents: false
    }, () => {
        const { triggeredEvents, contentIds } = instance.state;

        window.triggeredEvents = triggeredEvents;
        window.contentIds = contentIds;

        window.addEventListener('scroll', handlePromotionEventsTriggerOnScroll);

        return callback(...args);
    });
};

export const componentDidUpdate = (args, callback, instance) => {
    const { isLoading } = instance.props;
    const { triggeredEvents, handledPromotionEvents, contentIds } = instance.state;

    if (!isLoading && !handledPromotionEvents) {
        instance.setState({
            handledPromotionEvents: true
        });

        setTimeout(() => {
            const contents = document.querySelectorAll(CONTENTS_CUSTOM_CSS_CLASS);

            handlePromotionEventsTrigger(triggeredEvents, contents);
            addClickEventListenerToContents(contentIds, contents);
        }, TRIGGER_PROMOTION_EVENT_DELAY);
    }

    return callback(...args);
};

export const componentWillUnmount = (args, callback) => {
    const contents = document.querySelectorAll(CONTENTS_CUSTOM_CSS_CLASS);

    window.removeEventListener('scroll', handlePromotionEventsTriggerOnScroll);

    window.triggeredEvents = null;
    window.contentIds = null;

    contents.forEach((content) => {
        content.removeEventListener('click', handlePromotionEventsTriggerOnClick);
    });

    callback(...args);
};

export default {
    'Route/CategoryPage/Component': {
        'member-function': {
            componentDidMount,
            componentDidUpdate,
            componentWillUnmount
        }
    },
    'Scandipwa/Route/CmsPage/Component': {
        'member-function': {
            componentDidMount,
            componentDidUpdate,
            componentWillUnmount
        }
    }
};
